import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Check,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Eye,
    GripVertical,
    ImageIcon,
    Loader2,
    Link2,
    MoreHorizontal,
    Pencil,
    Shuffle,
    Trash2,
    ArrowDownUp,
    Images,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

import { ProductView } from './product-view.component';
import { resolveApplicationTypeLabel, dedupeApplicationTypes } from '../../../../common/utils/application-type';
import { useVendorFamilies } from '../../../../common/hooks/use-vendor-families.hook';
import { resolveTranslation } from '../../../../common/utils/translatable-string';
import { config } from '../../../../common/hooks/use-settings-api.hook';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../../common/utils/visualizer-link';
import { PriceText } from '../../../../components/design-system/common/PriceText';

// Column configuration for dynamic table headers
interface DynamicColumn {
    key: string;
    label: string;
    type: 'variant_axis' | 'attribute' | 'visualization';
    width?: string;
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

/** A small neutral pill for a single cell value. */
const CellPill = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-block bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
        {children}
    </span>
);

/** What a cell shows when there is nothing to show. */
const CellEmpty = () => <span className="text-[11px] text-muted-foreground">-</span>;

export const ProductsAsList = (props) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { families, getFamilyById } = useVendorFamilies();

    // Get current family for dynamic columns
    const currentFamily = props.selectedFamilyId ? getFamilyById(props.selectedFamilyId) : (families.length > 0 ? families[0] : null);

    // Build dynamic columns based on family configuration
    const getDynamicColumns = (): DynamicColumn[] => {
        if (!currentFamily) {
            // Fallback to default columns if no family is available
            return [
                { key: 'type', label: t('PAGES.MY_PRODUCTS.TABLE.H_TYPE'), type: 'visualization', width: '100px' },
                { key: 'application_types', label: t('PAGES.MY_PRODUCTS.TABLE.H_SURFACE'), type: 'visualization', width: '100px' },
                { key: 'size', label: t('PAGES.MY_PRODUCTS.TABLE.H_SIZE'), type: 'variant_axis', width: '100px' }
            ];
        }

        const columns: DynamicColumn[] = [];

        // Add product type column if enabled in visualizationConfig
        if (currentFamily.visualizationConfig?.showProductType !== false) {
            columns.push({
                key: 'type',
                label: t('PAGES.MY_PRODUCTS.TABLE.H_TYPE'),
                type: 'visualization',
                width: '100px'
            });
        }

        // Add application types column if enabled in visualizationConfig
        if (currentFamily.visualizationConfig?.showApplicationTypes !== false) {
            columns.push({
                key: 'application_types',
                label: t('PAGES.MY_PRODUCTS.TABLE.H_SURFACE'),
                type: 'visualization',
                width: '100px'
            });
        }

        // Add size column if size variant axis is enabled
        const sizeAxis = currentFamily.variantAxes?.find(axis => axis.name === 'size' && axis.enabled);
        if (sizeAxis) {
            columns.push({
                key: 'size',
                label: resolveTranslation(sizeAxis.label, 'en') || t('PAGES.MY_PRODUCTS.TABLE.H_SIZE'),
                type: 'variant_axis',
                width: '100px'
            });
        }

        // Add family-specific attributes that are visible in list
        // Exclude attributes that duplicate existing columns (name is already shown)
        const excludedFieldNames = ['name', 'product_name', 'productName', 'thumbnail', 'thumbnailPath', 'image', 'collections', 'collection'];
        const visibleAttributes = currentFamily.attributes?.filter(attr =>
            attr.visibleInList && !excludedFieldNames.includes(attr.fieldName)
        ) || [];
        visibleAttributes.forEach(attr => {
            columns.push({
                key: attr.fieldName,
                label: resolveTranslation(attr.fieldLabel, 'en'),
                type: 'attribute',
                width: attr.fieldName === 'brand' ? '140px' : '100px'
            });
        });

        return columns;
    };

    const dynamicColumns = getDynamicColumns();

    // Render cell content based on column type
    const renderCellContent = (product: any, column: DynamicColumn) => {
        switch (column.key) {
            case 'type':
                return product.type ? (
                    <CellPill>{t('TYPES.' + product.type.toUpperCase(), { defaultValue: product.type })}</CellPill>
                ) : (
                    <CellEmpty />
                );

            case 'application_types': {
                const types = dedupeApplicationTypes(product.application_types || []);
                if (types.length === 0) return <CellEmpty />;
                const label = types
                    .map(type => resolveApplicationTypeLabel(t, type))
                    .join(' / ');
                return <span className="text-xs font-medium">{label}</span>;
            }

            case 'size': {
                // Skip the size pill entirely when no dimensions are known
                // (e.g. draft products synced from an integration before the
                // vendor has filled in axes). Previously rendered as the
                // literal string "undefinedxundefined cm".
                if (product.width == null && product.length == null && product.height == null) {
                    return <CellEmpty />;
                }
                const dims = [product.width, product.length, product.height].filter(
                    (v) => v != null && v > 0,
                );
                if (dims.length === 0) return <CellEmpty />;
                return (
                    <span className="text-xs font-medium">
                        {dims.join('x')} {product.unit || 'cm'}
                    </span>
                );
            }

            default: {
                // Handle family-specific attributes
                const value = product.attributes?.[column.key] || product[column.key];
                if (value === undefined || value === null || value === '') return <CellEmpty />;

                // Handle array values
                if (Array.isArray(value)) {
                    return (
                        <div className="flex flex-wrap gap-1">
                            {value.map((v, idx) => (
                                <span
                                    key={idx}
                                    className="inline-flex items-center rounded-md border bg-muted px-1.5 py-0.5 text-[11px] font-medium"
                                >
                                    {v}
                                </span>
                            ))}
                        </div>
                    );
                }

                if (column.key === 'brand') {
                    return <span className="whitespace-nowrap text-xs font-medium">{String(value)}</span>;
                }

                return <CellPill>{String(value)}</CellPill>;
            }
        }
    };

    const [selectedProductIdForView, setSelectedProductIDForView] = useState(null);
    const [productViewAnchor, setProductViewAnchor] = useState(null);
    // The rows as drawn. A copy only so a manual drag can reorder them before saving.
    const [displayedProductsOrder, setdisplayedProductsOrder] = useState(props.filteredProducts);

    const [isSortOngoing, setIsSortOngoing] = useState(false)
    const [isSortManual, setIsSortManual] = useState(false)
    const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null)
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
    const [pendingSortConfig, setPendingSortConfig] = useState<string | null>(null)
    const [selectedProductForMenu, setSelectedProductForMenu] = useState(null)

    useEffect(() => {
        setdisplayedProductsOrder(props.filteredProducts);
    }, [props.filteredProducts]);

    /*
     * Sorting is the server's job now: a preview asks the parent to reload the
     * list in that order, and Validate saves it as the vendor's preference.
     * Only a manual reorder needs every product in hand, and the parent loads
     * them for exactly that.
     */
    const handleSortPreview = (configValue: string) => {
        props.onSortPreview?.(configValue);
        setPendingSortConfig(configValue);
        setIsSortOngoing(true);
        setIsSortManual(false);
    };

    const handleManualSortStart = () => {
        props.onManualSortStart?.();
        setIsSortOngoing(true)
        setIsSortManual(true)
    }

    const handleValidateSort = () => {
        setIsSortOngoing(false)

        if (isSortManual) {
            setIsSortManual(false)
            props.handleSaveManualSort(displayedProductsOrder.map((product, index) => ({ id: product.id, data: { order: index + 1 } })))
        } else if (pendingSortConfig) {
            props.handleSaveSortConfig(pendingSortConfig)
            setPendingSortConfig(null)
        }
    }

    const handleCancelSort = () => {
        setIsSortManual(false)
        setIsSortOngoing(false)
        setPendingSortConfig(null)
        props.onSortCancel?.()
    }

    // Native HTML5 drag and drop — row stays in the table at all times
    const handleRowDragStart = (e: React.DragEvent, index: number) => {
        setDragSourceIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleRowDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) setDragOverIndex(index);
    };

    const handleRowDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        if (dragSourceIndex === null || dragSourceIndex === targetIndex) {
            setDragSourceIndex(null);
            setDragOverIndex(null);
            return;
        }

        const reorderedProducts = Array.from(displayedProductsOrder);
        const [movedProduct] = reorderedProducts.splice(dragSourceIndex, 1);
        reorderedProducts.splice(targetIndex, 0, movedProduct);
        setdisplayedProductsOrder(reorderedProducts);

        setDragSourceIndex(null);
        setDragOverIndex(null);
    };

    const handleRowDragEnd = () => {
        setDragSourceIndex(null);
        setDragOverIndex(null);
    };

    const sortOptions = [
        { key: 'NAME_ASC', label: t('PAGES.MY_PRODUCTS.SORT.NAME_ASC') },
        { key: 'NAME_DESC', label: t('PAGES.MY_PRODUCTS.SORT.NAME_DESC') },
        { key: 'DATE_OLD_NEW', label: t('PAGES.MY_PRODUCTS.SORT.DATE_OLD_NEW') },
        { key: 'DATE_NEW_OLD', label: t('PAGES.MY_PRODUCTS.SORT.DATE_NEW_OLD') },
    ];

    // The parent hands over exactly the rows to draw: one page, or every product
    // while a manual reorder is open.
    const visibleRows = displayedProductsOrder;

    const totalCount = props.totalCount ?? displayedProductsOrder.length;
    const pageCount = Math.max(1, Math.ceil(totalCount / props.rowsPerPage));
    const firstRow = totalCount === 0 ? 0 : props.page * props.rowsPerPage + 1;
    const lastRow = Math.min(totalCount, (props.page + 1) * props.rowsPerPage);

    return (
        <>
            <div className="max-h-[70vh] overflow-auto rounded-xl border">
                <Table>
                    <TableHeader className="sticky top-0 z-10 bg-muted">
                        <TableRow className="hover:bg-transparent">
                            {isSortManual && <TableHead className="w-10" />}
                            {props.isSelectionMode && <TableHead className="w-12" />}
                            <TableHead className="w-20" />
                            <TableHead>{t('PAGES.MY_PRODUCTS.TABLE.H_NAME')}</TableHead>
                            {dynamicColumns.map(column => (
                                <TableHead key={column.key} style={{ width: column.width }}>
                                    {column.label}
                                </TableHead>
                            ))}
                            <TableHead className="w-40 text-end">
                                {isSortOngoing ? (
                                    <div className="flex justify-end gap-2">
                                        <Button variant="outline" size="sm" onClick={handleCancelSort}>
                                            {t('COMMON.CANCEL')}
                                        </Button>
                                        {/* Never save a manual order over a list still loading. */}
                                        <Button size="sm" onClick={handleValidateSort} disabled={!!props.sortLoading}>
                                            {props.sortLoading ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                                            {t('COMMON.VALIDATE')}
                                        </Button>
                                    </div>
                                ) : (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="icon-sm"
                                                className="ms-auto"
                                                aria-label={t('PAGES.MY_PRODUCTS.SORT.LABEL', 'Sort products') as string}
                                            >
                                                <ArrowDownUp className="size-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-52">
                                            {sortOptions.map(option => (
                                                <DropdownMenuItem
                                                    key={option.key}
                                                    onClick={() => handleSortPreview(option.key)}
                                                >
                                                    <ArrowDownUp className="size-4" />
                                                    <span className="flex-1">{option.label}</span>
                                                    {props.productsOrder === option.key && <Check className="size-4" />}
                                                </DropdownMenuItem>
                                            ))}
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => handleSortPreview('DISCOVERY')}>
                                                <Shuffle className="size-4" />
                                                <span className="flex-1">{t('PAGES.MY_PRODUCTS.SORT.DISCOVERY')}</span>
                                                {props.productsOrder === 'DISCOVERY' && <Check className="size-4" />}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={handleManualSortStart}>
                                                <GripVertical className="size-4" />
                                                <span className="flex-1">{t('PAGES.MY_PRODUCTS.SORT.MANUAL')}</span>
                                                {props.productsOrder === 'MANUAL' && <Check className="size-4" />}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </TableHead>
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {visibleRows.map((product, index) => (
                            <TableRow
                                key={product.id ?? product._id ?? index}
                                draggable={isSortManual}
                                onDragStart={isSortManual ? (e) => handleRowDragStart(e, index) : undefined}
                                onDragOver={isSortManual ? (e) => handleRowDragOver(e, index) : undefined}
                                onDrop={isSortManual ? (e) => handleRowDrop(e, index) : undefined}
                                onDragEnd={isSortManual ? handleRowDragEnd : undefined}
                                className={cn(
                                    'h-[60px] transition-colors',
                                    isSortManual && 'cursor-grab',
                                    dragSourceIndex === index && 'opacity-40',
                                    // The drop target is marked by a line above it rather than by
                                    // moving rows around, so the table never reflows mid-drag.
                                    dragOverIndex === index &&
                                        dragSourceIndex !== null &&
                                        dragSourceIndex !== index &&
                                        'shadow-[inset_0_2px_0_0_var(--foreground)]',
                                )}
                            >
                                {isSortManual && (
                                    <TableCell className="w-10">
                                        <GripVertical className="size-5 cursor-grab text-muted-foreground active:cursor-grabbing" />
                                    </TableCell>
                                )}

                                {props.isSelectionMode && (
                                    <TableCell className="w-12">
                                        <Checkbox
                                            checked={props.selectedProductIds?.has(product.id) || false}
                                            onCheckedChange={() => props.onSelectProduct?.(product.id)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </TableCell>
                                )}

                                <TableCell className="w-20">
                                    <div className="relative w-fit">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                setProductViewAnchor(e.currentTarget);
                                                setSelectedProductIDForView(product.uuid);
                                            }}
                                            className="block size-13 overflow-hidden rounded-lg border transition-transform hover:scale-105"
                                        >
                                            {product.thumbnailPath ? (
                                                <img
                                                    src={product.thumbnailPath}
                                                    className={cn(
                                                        'size-full',
                                                        !product.familySlug || product.familySlug === 'surface-products'
                                                            ? 'object-cover'
                                                            : 'object-contain',
                                                    )}
                                                    alt="product thumbnail"
                                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                                />
                                            ) : (
                                                <span className="flex size-full items-center justify-center bg-muted">
                                                    <ImageIcon className="size-6 text-muted-foreground" />
                                                </span>
                                            )}
                                        </button>

                                        {product.siblings?.length > 0 && (
                                            <span className="absolute -top-1.5 -end-1.5 flex h-5 min-w-6 items-center justify-center bg-foreground px-1 text-[10px] font-medium text-background">
                                                +{product.siblings?.length}
                                            </span>
                                        )}

                                        {product.isDraft && (
                                            <span
                                                data-testid="product-draft-badge"
                                                className="absolute -top-1.5 -start-1.5 flex h-[18px] items-center justify-center bg-warning px-1.5 text-[9px] font-medium tracking-wide text-white uppercase"
                                            >
                                                {t('PAGES.MY_PRODUCTS.DRAFT_BADGE', 'Draft')}
                                            </span>
                                        )}
                                    </div>

                                    {/* The variants that share this row, as overlapping dots. */}
                                    <div className="mt-2.5 flex">
                                        {(product.siblings || []).slice(0, 5).map(sibling => (
                                            <button
                                                type="button"
                                                key={sibling.id}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setProductViewAnchor(e.currentTarget);
                                                    setSelectedProductIDForView(sibling.uuid);
                                                }}
                                                className="-ms-1.5 transition-transform first:ms-0 hover:z-10 hover:-translate-y-0.5"
                                            >
                                                <span className="block size-5 overflow-hidden rounded-full border-2 border-background">
                                                    {sibling.thumbnailPath ? (
                                                        <img
                                                            src={sibling.thumbnailPath}
                                                            className="size-full object-cover"
                                                            alt="sibling thumbnail"
                                                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <span className="flex size-full items-center justify-center bg-muted">
                                                            <ImageIcon className="size-3 text-muted-foreground" />
                                                        </span>
                                                    )}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </TableCell>

                                <TableCell className="w-[300px] p-0">
                                    {/*
                                      A real link, so the row can be opened in a new tab or
                                      copied — the click handler only takes over the plain case.
                                    */}
                                    <a
                                        href={`/products/${product.uuid}/edit`}
                                        onClick={(e) => {
                                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                            e.preventDefault();
                                            props.handleEditProduct(product);
                                        }}
                                        className="block p-4 text-inherit no-underline"
                                    >
                                        {product.attributes?.collections && product.attributes.collections.length > 0 && (
                                            <div className="mb-1.5 flex flex-wrap gap-1">
                                                {product.attributes.collections.slice(0, 2).map((collection, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="inline-flex items-center rounded-md border px-1 py-0.5 text-[9px] font-medium tracking-wide uppercase"
                                                    >
                                                        {collection}
                                                    </span>
                                                ))}
                                                {product.attributes.collections.length > 2 && (
                                                    <span className="inline-flex items-center rounded-md border px-1 py-0.5 text-[9px] font-medium text-muted-foreground">
                                                        +{product.attributes.collections.length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <span className="font-medium hover:text-muted-foreground">{product.name}</span>
                                        {/* What shoppers see it sold for, wherever that came from; nothing when unknown. */}
                                        <PriceText
                                            price={product.pricing?.price}
                                            className="mt-0.5 block text-xs text-muted-foreground"
                                            data-testid="catalog-price"
                                        />
                                    </a>
                                </TableCell>

                                {dynamicColumns.map(column => (
                                    <TableCell key={column.key} style={{ width: column.width }}>
                                        {renderCellContent(product, column)}
                                    </TableCell>
                                ))}

                                <TableCell className="w-32 text-end">
                                    <div className="flex justify-end gap-1.5">
                                        {product.link && (
                                            <Button
                                                variant="outline"
                                                size="icon-sm"
                                                aria-label={t('PAGES.MY_PRODUCTS.OPEN_PRODUCT_LINK', 'Open product link') as string}
                                                onClick={() => window.open(product.link, '_blank')}
                                            >
                                                <Link2 className="size-4" />
                                            </Button>
                                        )}

                                        {/*
                                          One menu per row rather than a single shared one: an
                                          anchored menu needed a DOM node in state, which is what
                                          `selectedProductForMenu` was for. It survives only to
                                          hold the optimistic visibility flip below.
                                        */}
                                        <DropdownMenu
                                            onOpenChange={(open) => setSelectedProductForMenu(open ? product : null)}
                                        >
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    size="icon-sm"
                                                    aria-label={t('PAGES.MY_PRODUCTS.PRODUCT_ACTIONS', 'Product actions') as string}
                                                >
                                                    <MoreHorizontal className="size-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-52">
                                                <DropdownMenuItem
                                                    onSelect={(e) => e.preventDefault()}
                                                    className="justify-between"
                                                >
                                                    <span className="flex items-center gap-2">
                                                        <Eye className="size-4" />
                                                        {t('PAGES.MY_PRODUCTS.MENU.VISIBILITY')}
                                                    </span>
                                                    <Switch
                                                        checked={
                                                            (selectedProductForMenu?.id === product.id
                                                                ? selectedProductForMenu?.isVisible
                                                                : product.isVisible) || false
                                                        }
                                                        onCheckedChange={() => {
                                                            // Save, then mirror the change locally so the
                                                            // switch answers immediately — the list only
                                                            // refreshes once the request lands.
                                                            props.handleProductVisibilityChange(product);
                                                            const updated = Object.create(Object.getPrototypeOf(product));
                                                            Object.assign(updated, product);
                                                            updated.setIsVisible(!product.isVisible);
                                                            setSelectedProductForMenu(updated);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </DropdownMenuItem>

                                                <DropdownMenuItem onClick={() => props.handleEditProduct(product)}>
                                                    <Pencil className="size-4" />
                                                    {t('PAGES.MY_PRODUCTS.MENU.EDIT')}
                                                </DropdownMenuItem>

                                                {product.sku && config?.alias && !product.isDraft && (
                                                    <DropdownMenuItem
                                                        onClick={() => {
                                                            const url = buildVisualizerUrl({
                                                                alias: config.alias,
                                                                sku: product.sku,
                                                                attribution: PREVIEW_LINK_ATTRIBUTION,
                                                            });
                                                            window.open(url, '_blank', 'noopener,noreferrer');
                                                        }}
                                                    >
                                                        <ExternalLink className="size-4" />
                                                        {t('PAGES.MY_PRODUCTS.MENU.PREVIEW')}
                                                    </DropdownMenuItem>
                                                )}

                                                {/* Pictures, a clip and posts for this product (feat/room-to-revenue). */}
                                                {product.uuid && !product.isDraft && (
                                                    <DropdownMenuItem onClick={() => navigate(`/marketing/content-packs?make=${product.uuid}`)}>
                                                        <Images className="size-4" />
                                                        {t('CONTENT_PACKS.ENTRY')}
                                                    </DropdownMenuItem>
                                                )}

                                                <DropdownMenuSeparator />

                                                <DropdownMenuItem variant="destructive" onClick={() => props.onDelete(product)}>
                                                    <Trash2 className="size-4" />
                                                    {t('PAGES.MY_PRODUCTS.MENU.DELETE')}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/*
              Pagination, by hand. MUI's TablePagination has no shadcn counterpart,
              and the one in ui/pagination.tsx is link-based — wrong for a control
              that only moves state.
            */}
            {!isSortOngoing && (
                <div className="mt-4 flex flex-wrap items-center justify-end gap-4 rounded-xl border p-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                            {t('PAGES.MY_PRODUCTS.PAGINATION.ROWS_PER_PAGE')}
                        </span>
                        <Select
                            value={String(props.rowsPerPage)}
                            onValueChange={(value) => props.handleChangeRowsPerPage({ target: { value } })}
                        >
                            <SelectTrigger size="sm" className="w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROWS_PER_PAGE_OPTIONS.map(option => (
                                    <SelectItem key={option} value={String(option)}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <span className="text-xs text-muted-foreground">
                        {firstRow}-{lastRow} {t('PAGES.MY_PRODUCTS.PAGINATION.OF')} {totalCount}
                    </span>

                    <div className="flex gap-1.5">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            disabled={props.page === 0}
                            onClick={() => props.handleChangePage(null, props.page - 1)}
                            aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.PREVIOUS', 'Previous page')}
                        >
                            <ChevronLeft className="size-4 rtl:rotate-180" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon-sm"
                            disabled={props.page >= pageCount - 1}
                            onClick={() => props.handleChangePage(null, props.page + 1)}
                            aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.NEXT', 'Next page')}
                        >
                            <ChevronRight className="size-4 rtl:rotate-180" />
                        </Button>
                    </div>
                </div>
            )}

            {selectedProductIdForView && (
                <ProductView
                    selectedProductForViewID={selectedProductIdForView}
                    setSelectedProductForView={setSelectedProductIDForView}
                    anchorEl={productViewAnchor}
                    setAnchorEl={setProductViewAnchor}
                />
            )}
        </>
    );
}
